<template>
  <div>
    <page-header
      :actions="headerActionsConfig"
      left-side-class="col-12 col-md-7"
      :page="headerConfig"
      right-side-class="col-12 col-md-5"
    />
    <filter-manager
      ref="filterRef"
      v-model="filterModel"
      class="mb-4"
      :filters="filtersConfig"
      @academicYear="onChangeAcademicYearHandler"
      @contractType="onChangeContractTypeHandler"
      @provider="onChangeProviderHandler"
      @search="onSearchHandler"
      @service="() => {}"
      @signatureType="onChangeSignatureType"
    />
    <custom-separator class="my-4" text="Resultado de Contratos" />
    <custom-result-not-found v-if="results.length == 0" />
    <div v-for="(row, index) of results" :key="`contract-item-${index}`">
      <result-row
        ref="contra-row-ref"
        :can-add-diagnosis="canAddDiagnosis"
        :can-register-intervention-plan="canRegisterInterventionPlan"
        :data="row"
        :medicaid-pilot-company-ids="medicaidPilotCompanyIds"
        @on-add-intervention-plan="onAddInterventionPlanHandler(row)"
        @on-compensatory-service-change-page="
          onCompensatoryRequestChangePageHandler(index, row, $event)
        "
        @on-complete-evaluation="onCompleteEvaluationHandler(index, row)"
        @on-cost-change-history-load-more="
          onCostChangeHistoryLoadMoreHandler(index, row)
        "
        @on-download-contract="onDownloadContractHandler(row)"
        @on-history-change-page="onHistoryChangePageHandler(index, row, $event)"
        @on-open-add-diagnosis-tab="onOpenAddDiagnosisHandler(index, row)"
        @on-open-aee-tab="onOpenAeeTabHandler(index, row)"
        @on-open-close="onOpenCloseHandler(index, row)"
        @on-open-compensatory-request-tab="
          onOpenCompensatoryRequestTabHandler(index, row, 1, 5)
        "
        @on-open-compensatory-therapy-balance-tab="
          onOpenCompensatoryTherapyBalanceHandler(index, row)
        "
        @on-open-contract-additional-tab="
          onOpenAdditionalInfoTabHandler(index, row)
        "
        @on-open-contract-history-tab="
          onOpenHistoryTabHandler(index, row, 1, 5)
        "
        @on-open-cost-change-history-tab="
          onOpenCostChangeHistoryTabHandler(index, row, 1, 3)
        "
        @on-open-document-history-tab="
          onOpenDocumentHistoryTabHandler(index, row)
        "
        @on-open-documents-tab="onOpenDocumentsTabHandler(index, row)"
        @on-open-evaluation-documents-tab="
          onViewEvaluationDocumentsHandler(index, $event)
        "
        @on-open-modify-approval-date="
          onOpenModifyApprovalDateHandler(index, row)
        "
        @on-open-return-to-new-status="
          onOpenReturnToNewStatusHandler(index, row, $event)
        "
        @on-open-return-to-previous-status="
          onOpenReturnToPreviousStatusHandler(index, row, $event)
        "
        @on-open-status-history-tab="
          onOpenStatusHistoryTabHandler(index, row, 1, 3)
        "
        @on-open-visit-detail-tab="onOpenVisitDetailTabHandler(index, row)"
        @on-refresh-addiotionals-documents="
          onRefreshAdditionalDocumentsHandler(index, row)
        "
        @on-register-visits="onViewRegisterVisitsHandler(row)"
        @on-return-contract-to-new-status="searchContracts"
        @on-service-certification="onViewServiceCertification(row)"
        @on-service-start-date-save="searchContracts"
        @on-status-history-load-more="
          onStatusHistoryLoadMoreHandler(index, row)
        "
        @on-view-achievement-summary="onViewAchievementSummary(row)"
        @on-view-contract="onViewContractHandler(row)"
      />
      <custom-error
        v-if="row.isStudentEgressed"
        class="mt-0"
        :message="`Estudiante Egresado el ${$formatDate(row.egressedDate)}`"
        variant="border"
      />
      <RowMessage
        v-if="
          !row.contractMatchWithPei &&
            isTherapyService(row) &&
            !isTherapyCompensatory(row)
        "
        class="mt-0"
        :text="`Se han realizado cambios en el PEI del estudiante.`"
        warning
      />
    </div>
    <pagination
      v-if="showPagination"
      :data="pagination"
      show-text
      @pagination-go-page="goToPageHandler"
    />
  </div>
</template>

<script>
//libs components
import PageHeader from 'PageHeader';
import FilterManager from 'FilterManager';
import CustomError from 'custom-error';

import CustomSeparator from 'custom-separator';
import CustomResultNotFound from 'custom-result-not-found';
import Pagination from 'pagination';
import requestHistoryViewDisplay from '@/utils/constants/requestHistoryViewDisplay.js';
import {
  getByParameterName,
  getSysparameterByNames,
} from '@/services/common/sysparameter.api';

//local components
import { ResultRow } from './components';
import RowMessage from '@/components/common/RowMessage.vue';

//services
import {
  getInitFilters as _getInitFilters,
  searchContracts as _searchContracts,
  getProviders as _getProviders,
  getContractStatusHistory as _getContractStatusHistory,
  getContractHistory as _getContractHistory,
  getAdditionalsDocumentTypes as _getAdditionalsDocumentTypes,
  getAdditionalContractDocuments as _getAdditionalContractDocuments,
  getContractDocumentsHistory as _getContractDocumentsHistory,
  getContractVisitInfo as _getContractVisitInfo,
  getBalanceOfTherapyCompensatory as _getBalanceOfTherapyCompensatory,
  getStudentLocations as _getStudentLocations,
  getContractCostChangeHistory as _getContractCostChangeHistory,
  getContractAdditionalInfo as _getContractAdditionalInfo,
  downloadSignedContractDocument as _downloadSignedContractDocument,
  getContractPreviousStatus as _getContractPreviousStatus,
  getContractInitEvaluationDocument as _getContractInitEvaluationDocument,
  getSubmittedContractDate as _getSubmittedContractDate,
} from '@/services/uspqrp/contract.api.js';
import { getSpecialistsByProvider as _getSpecialistsByProvider } from '@/services/provider/specialist.api.js';
import { getDisciplineTypes as _getDisciplineTypes } from '@/services/uspqrp/provisional-remedy.api.js';
import { getRequestExtendedAcademicYear as _getRequestExtendedAcademicYear } from '@/services/provider/extended-academic-year.api.js';
import { searchByContractId as _searchByContractId } from '@/services/provider/compensatoryRequest.api.js';
import ReportServices from '@/services/common/report.api';

//utils
import alertMixin from '@quisit/mipe-utils/mixins/alert.mixin.js';
import storeMixin from '@quisit/mipe-utils/mixins/storeGenerator';
import utils from '@/utils/utilities';

//constants
import storeModuleNames from '@/utils/constants/storeModuleNames.js';
import contractViewDisplay from '@/utils/constants/contractViewDisplay.js';
import rpServiceTypes from '@/utils/constants/serviceTypes.js';
import rpContractStatus from '@/utils/constants/rpContractStatus.js';
import compensatoryRequestStatus from '@/utils/constants/compensatoryRequestStatus';
const DEFAULT_PAGE_SIZE = 20;
const DEFAULT_HISTORY_STATUS_PAGE_SIZE = 3;
const DEFAULT_HISTORY_PAGE_SIZE = 5;
const SEARCH_REQUEST_ROUTE_NAME = 'searchRequest';
const CONTRACT_MANAGEMENT_ROUTE_NAME = 'contractManagement';
const VISIT_REGISTRY_ROUTE_NAME = 'visitRegistry';
const INTERVENTION_PLAN_MANAGEMENT_ROUTE_NAME = 'interventionPlanManagement';
const ACHIEVEMENT_SUMMARY_ROUTE_NAME = 'achievementSummary';

export default {
  name: 'ContractManagement',
  mixins: [storeMixin, alertMixin],
  components: {
    PageHeader,
    FilterManager,
    CustomSeparator,
    CustomResultNotFound,
    Pagination,
    ResultRow,
    CustomError,
    RowMessage,
  },
  data: () => ({
    moduleName: storeModuleNames.UspqrpContractModuleName,
    filterModel: {
      academicYear: null,
      provider: null,
      specialist: null,
      contractType: null,
      disciplineType: null,
      contractStatus: null,
      sieOrRegistryNumber: null,
      studentName: null,
      requestNumber: null,
      contractNumber: null,
      isSortByStudentNameAsc: true,
      sortBy: null,
      hasAEEQuestionnaire: false,
      hasCompensatoryServiceForm: false,
      isElectronicSignature: false,
      alertType: null,
    },
    filterOptions: {
      academicYears: [],
      providers: [],
      specialists: [],
      contractTypes: [],
      disciplineTypes: [],
      contactStatuses: [],
      signatureTypes: [],
      alertTypes: [],
      studentLocations: [],
    },
    results: [],
    pagination: {
      currentPage: 1,
      totalItemCount: 1,
      pageCount: 1,
      count: 1,
      pageSize: DEFAULT_PAGE_SIZE,
    },
    requireAcademicYear: null,
    aeeFeaturesEnabled: false,
    canRegisterInterventionPlan: false,
    canAddDiagnosis: false,
    medicaidPilotCompanyIds: [],
  }),
  computed: {
    headerConfig() {
      return {
        title: 'Contrato',
        icon: 'fas fa-clipboard-list',
        routes: ['Inicio', 'Proveedores', 'Contrato'],
      };
    },
    headerActionsConfig() {
      return [
        {
          text: 'Añadir Contrato',
          icon: 'fas fa-file-plus pr-2',
          variant: 'success ml-0',
          visible: this.userHasPermissions('rempro.providers.contract.add'),
          click: () => this.onAddContractHandler(),
        },
      ];
    },
    filtersConfig() {
      return [
        {
          name: 'academicYear',
          component: 'CustomDropDown',
          options: this.filterOptions.academicYears,
          placeholder: 'Año Académico',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-4',
          clearable: false,
        },
        {
          name: 'provider',
          component: 'CustomDropDown',
          options: this.filterOptions.providers,
          placeholder: 'Proveedor',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-4',
          validations: this.requireAcademicYear ? 'required' : '',
          error: 'El nombre del proveedor es requerido.',
          clearable: true,
          disabled: this.hasOnlyOneCompanyToFilter,
        },
        {
          name: 'specialist',
          component: 'CustomDropDown',
          options: this.filterOptions.specialists,
          placeholder: 'Especialista',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-4',
          clearable: true,
          disabled: this.hasOneSpecialistToFilter,
        },
        {
          name: 'contractType',
          component: 'CustomDropDown',
          options: this.filterOptions.contractTypes,
          label: 'Tipo de Contrato:',
          placeholder: 'Seleccione',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-3',
          clearable: true,
          isAdvanced: true,
        },
        {
          name: 'disciplineType',
          component: 'CustomDropDown',
          options: this.filterOptions.disciplineTypes,
          label: 'Disciplina:',
          placeholder: 'Seleccione',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-3',
          clearable: true,
          isAdvanced: true,
        },
        {
          name: 'contractStatus',
          component: 'CustomDropDown',
          options: this.filterOptions.contractStatuses,
          label: 'Estatus del Contrato:',
          placeholder: 'Seleccione',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-3',
          clearable: true,
          isAdvanced: true,
        },
        {
          name: 'sieOrRegistryNumber',
          component: 'CustomInput',
          icon: 'fas fa-search color-primary-2',
          leftIcon: true,
          label: 'Número de SIE o Registro:',
          placeholder: 'Escriba Número de SIE o Registro',
          class: 'col-12 col-md-3',
          isAdvanced: true,
        },
        {
          name: 'requestNumber',
          component: 'CustomInput',
          icon: 'fas fa-search color-primary-2',
          leftIcon: true,
          label: 'Número de Solicitud:',
          placeholder: 'Escriba Número de Solicitud',
          class: 'col-12 col-md-3',
          isAdvanced: true,
        },
        {
          name: 'contractNumber',
          component: 'CustomInput',
          icon: 'fas fa-search color-primary-2',
          leftIcon: true,
          label: 'Número de Contrato:',
          placeholder: 'Escriba Número de Contrato',
          class: 'col-12 col-md-3',
          isAdvanced: true,
        },
        {
          name: 'hasAEEQuestionnaire',
          component: 'CustomDropDown',
          options: this.filterOptions.hasAEEQuestionnaireOptions,
          label: '¿Tiene cuestionario de AEE?',
          placeholder: 'Seleccione',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-3',
          clearable: true,
          isAdvanced: true,
          visible: this.aeeFeaturesEnabled,
        },
        {
          name: 'hasCompensatoryServiceForm',
          component: 'CustomDropDown',
          options: this.filterOptions.hasCompensatoryServiceFormOptions,
          label: '¿Tiene planillas de servicios compensatorios?',
          placeholder: 'Seleccione',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-3',
          clearable: true,
          isAdvanced: true,
          visible: this.aeeFeaturesEnabled,
        },
        {
          name: 'studentName',
          component: 'CustomInput',
          icon: 'fas fa-search color-primary-2',
          leftIcon: true,
          label: 'Nombre de Estudiante:',
          placeholder: 'Escriba Nombre de estudiante',
          class: 'col-12 col-md-3',
          isAdvanced: true,
        },
        {
          name: 'signatureType',
          component: 'CustomDropDown',
          options: this.filterOptions.signatureTypes,
          label: 'Tipo de Firma:',
          placeholder: 'Seleccione',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-3',
          clearable: true,
          isAdvanced: true,
        },
        {
          name: 'alertType',
          component: 'CustomDropDown',
          options: this.filterOptions.alertTypes,
          label: 'Alerta:',
          placeholder: 'Seleccione',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-3',
          clearable: true,
          isAdvanced: true,
        },
        {
          name: 'studentCenter',
          component: 'CustomDropDown',
          options: this.filterOptions.studentLocations,
          label: 'Centro del estudiante:',
          placeholder: 'Seleccione',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-3',
          clearable: true,
          isAdvanced: true,
        },
      ];
    },
    sortIcon() {
      return this.filterModel.isSortByStudentNameAsc ? 'down' : 'up';
    },
    showPagination() {
      return this.pagination.totalItemCount > this.pagination.pageSize;
    },
    hasPersistInfo() {
      return Boolean(this.moduleData?.ContractManagement || false);
    },
    userCurrentCompany() {
      return this.userCompanies?.length === 1 ? this.userCompanies[0] : null;
    },
    hasOnlyOneCompanyToFilter() {
      return this.filterOptions.providers?.length === 1;
    },
    hasOneSpecialistToFilter() {
      return this.filterOptions?.specialists?.length === 1;
    },
    isTherapyService() {
      return ({ requestServiceTypeId }) =>
        requestServiceTypeId === rpServiceTypes.Therapy;
    },
    isTherapyCompensatory() {
      return ({ isTherapyCompensatory }) => isTherapyCompensatory;
    },
  },
  async mounted() {
    await this.getInitFilters();
    await Promise.all([
      this.getAEESysParameter(),
      this.getAddDiagnosisSysParameter(),
      this.getCanRegisterInterventionPlanParameter(),
    ]);
  },
  destroyed() {
    this.persistData();
  },
  methods: {
    async getInitFilters() {
      const { data: response } = await _getInitFilters();
      if (Object.values(response).length) {
        const { filters, collections } = response;
        this.filterOptions = {
          ...this.filterOptions,
          ...collections,
          providers: this.userCompanies?.length
            ? this.userCompanies
            : collections.providers,
          hasAEEQuestionnaireOptions: [
            {
              id: 1,
              name: 'SI',
              value: true,
            },
            {
              id: 0,
              name: 'NO',
              value: false,
            },
          ],
          hasCompensatoryServiceFormOptions: [
            {
              id: 1,
              name: 'SI',
              value: true,
            },
            {
              id: 0,
              name: 'NO',
              value: false,
            },
          ],
          sortByOptions: [
            {
              id: 1,
              name: 'Fecha de Estatus Actual (A-Z): ASC',
              value: 1,
            },
            {
              id: 2,
              name: 'Fecha de Estatus Actual (Z-A): DESC',
              value: 2,
            },
          ],
        };
        this.filterModel = {
          ...this.filterModel,
          academicYear: this.hasPersistInfo
            ? this.filterModel.academicYear
            : {
                id: filters.currentYear.id,
                name: filters.currentYear.description,
              },
        };
        await this.getProviders(this.filterModel.academicYear.id);
        await this.getStudentLocations(this.filterModel.academicYear.id);

        if (this.hasOnlyOneCompanyToFilter) {
          await this.getSpecialists(
            this.filterModel.academicYear.id,
            this.filterModel.provider.id
          );
        }
      }
    },
    async getProviders(academicYearId) {
      if (academicYearId) {
        this.filterOptions.providers = (
          await _getProviders(academicYearId)
        ).data;

        if (this.hasOnlyOneCompanyToFilter) {
          this.filterModel.provider = this.filterOptions.providers[0];
        }
      }
    },
    async getStudentLocations(academicYearId) {
      if (academicYearId) {
        this.filterOptions.studentLocations = (
          await _getStudentLocations(academicYearId)
        ).data;
      }
    },
    async getSpecialists(academicYearId, providerId) {
      if (academicYearId && providerId) {
        this.filterOptions.specialists = (
          await _getSpecialistsByProvider(academicYearId, providerId, true)
        ).data;

        if (this.hasOneSpecialistToFilter) {
          this.filterModel.specialist = this.filterOptions.specialists[0];
        }
      }
    },
    async onChangeAcademicYearHandler(value) {
      this.filterModel.provider = null;
      this.filterModel.specialist = null;
      this.filterOptions.specialists = [];
      if (value?.id) {
        await this.getProviders(value.id);
      }

      if (this.hasOnlyOneCompanyToFilter) {
        await this.getSpecialists(
          this.filterModel.academicYear.id,
          this.filterModel.provider.id
        );
      }
    },
    async onChangeProviderHandler(value) {
      this.filterModel.specialist = null;
      this.filterOptions.specialists = [];
      if (value?.id) {
        await this.getSpecialists(this.filterModel.academicYear?.id, value.id);
      }
    },
    async onChangeContractTypeHandler(value) {
      this.filterModel.disciplineType = null;
      if (value?.id) {
        this.filterOptions.disciplineTypes = (
          await _getDisciplineTypes(value.id)
        ).data;
      }
    },
    async onChangeSignatureType(value) {
      this.filterModel.isElectronicSignature = value?.id;
    },
    async searchContracts() {
      const payload = {
        academicYearId: this.filterModel.academicYear?.id,
        providerId: this.filterModel.provider?.id,
        specialistId: this.filterModel.specialist?.id,
        contractTypeId: this.filterModel.contractType?.id,
        disciplineTypeId: this.filterModel.disciplineType?.id,
        contractStatusId: this.filterModel.contractStatus?.id,
        sieOrRegistryNumber: this.filterModel.sieOrRegistryNumber
          ?.trim()
          ?.toLocaleLowerCase(),
        studentName: this.filterModel.studentName,
        requestNumber: this.filterModel.requestNumber?.trim(),
        contractNumber: this.filterModel.contractNumber?.trim(),
        isSortByStudentNameAsc: this.filterModel.isSortByStudentNameAsc,
        sortBy: this.filterModel.sortBy?.value,
        hasAEEQuestionnaire: this.filterModel.hasAEEQuestionnaire?.value,
        hasCompensatoryServiceForm: this.filterModel.hasCompensatoryServiceForm
          ?.value,
        pageIndex: this.pagination.currentPage,
        pageSize: this.pagination.pageSize,
        signatureType: this.filterModel.signatureType
          ? this.filterModel.signatureType.id
          : null,
        alertTypeId: this.filterModel.alertType?.id,
        studentCenterId: this.filterModel.studentCenter?.id,
      };
      const { data: response } = await _searchContracts(payload);

      if (Object.values(response).length) {
        this.results = response.data.map((x) => ({
          ...x,
          aeeFeaturesEnabled: this.aeeFeaturesEnabled,
          contractStatusHistory: [],
          contractHistory: {
            data: [],
            pagination: {},
          },
          requestAee: {
            requestId: 0,
            statusId: 0,
            reasons: null,
            academicYear: null,
            status: null,
            comments: null,
            createdDate: null,
            requestDate: null,
            requestNumber: null,
            documents: [],
          },
          compensatoryRequest: {
            data: [],
            isEnabled: this.aeeFeaturesEnabled,
            pagination: {},
          },
          contractCostChangeHistory: [],
        }));
        this.pagination = {
          ...this.pagination,
          totalItemCount: response.totalItemCount,
          pageCount: response.pageCount,
          currentPage: response.currentPage,
          count: response.count,
        };
      }
    },
    async getContractStatusHistory(
      contractId,
      pageIndex,
      pageSize = DEFAULT_HISTORY_STATUS_PAGE_SIZE
    ) {
      return (await _getContractStatusHistory(contractId, pageIndex, pageSize))
        .data;
    },
    async getContractCostChangeHistory(
      contractId,
      pageIndex,
      pageSize = DEFAULT_HISTORY_STATUS_PAGE_SIZE
    ) {
      return (
        await _getContractCostChangeHistory(contractId, pageIndex, pageSize)
      ).data;
    },
    async getContractHistory(
      contractId,
      pageIndex,
      pageSize = DEFAULT_HISTORY_PAGE_SIZE
    ) {
      return (await _getContractHistory(contractId, pageIndex, pageSize)).data;
    },
    async getRequestExtendedAcademicYear(contractId) {
      return (await _getRequestExtendedAcademicYear(contractId)).data;
    },
    async searchByContractId(
      contractId,
      excludedStatusIds,
      pageIndex,
      pageSize = DEFAULT_HISTORY_PAGE_SIZE
    ) {
      return (
        await _searchByContractId(contractId, {
          excludedStatusIds,
          pageIndex,
          pageSize,
        })
      ).data;
    },
    async onSearchHandler() {
      await this.raiseRequireAcademicYearField(false);

      this.pagination.currentPage = 1;
      this.filterModel.isSortByStudentNameAsc = true;
      this.searchContracts();
      await this.getCanRegisterInterventionPlanParameter();
    },
    async goToPageHandler(page) {
      this.pagination.currentPage = page;
      await this.searchContracts();
    },
    async raiseRequireAcademicYearField(value) {
      this.requireAcademicYear = value;
      await this.$forceUpdate();

      if (!value) {
        this.$refs.filterRef.$refs.formValidator.reset();
      }
    },
    async onAddContractHandler() {
      await this.raiseRequireAcademicYearField(true);

      const isValid = await this.$refs.filterRef.$refs.formValidator.validate();
      if (!isValid) {
        this.ShowToast(
          'Alerta',
          'Favor completar la información solicitada.',
          'error'
        );
        return;
      }

      const companyId = this.filterModel.provider?.id;

      this.$router.push({
        name: SEARCH_REQUEST_ROUTE_NAME,
        params: {
          companyId,
        },
      });
    },
    onViewContractHandler({ companyId, requestServiceId, contractId }) {
      if (contractId) {
        this.$router.push({
          name: CONTRACT_MANAGEMENT_ROUTE_NAME,
          params: {
            companyId,
            requestServiceId,
            contractId,
          },
        });
      }
    },
    async onDownloadContractHandler({ contractId }) {
      await _downloadSignedContractDocument(contractId)
        .then((res) => utils.convertToBlob(res.data))
        .catch((err) => {
          this.ShowToast('Alerta', err.response?.data?.message, 'error');
        });
    },
    onViewRegisterVisitsHandler({
      companyId,
      requestServiceId,
      contractId,
      academicYearId,
    }) {
      if (contractId) {
        this.$router.push({
          name: VISIT_REGISTRY_ROUTE_NAME,
          params: {
            companyId,
            requestServiceId,
            contractId,
            academicYearId,
          },
        });
      }
    },
    async onOpenReturnToNewStatusHandler(index, { contractId }, event) {
      const { data: previousStatus } = await _getContractPreviousStatus(
        contractId
      ).catch(() => ({ data: null }));
      this.setActiveViewDisplay(
        requestHistoryViewDisplay.RETURN_TO_PREVIOUS_STATUS,
        index,
        { returnToNewStatus: event, previousStatus }
      );
    },
    async onOpenReturnToPreviousStatusHandler(index, { contractId }, event) {
      const { data: previousStatus } = await _getContractPreviousStatus(
        contractId
      ).catch(() => ({ data: null }));
      this.setActiveViewDisplay(
        requestHistoryViewDisplay.RETURN_TO_PREVIOUS_STATUS,
        index,
        { returnToPreviousStatus: event, previousStatus }
      );
    },
    async onOpenModifyApprovalDateHandler(index, { contractId }) {
      const [
        { data: submittedContractDate },
        { data: additionalInformation },
      ] = await Promise.all([
        _getSubmittedContractDate(contractId).catch((error) => {
          this.ShowToast('Alerta', `${error.message}.`, 'error');
          return { data: null };
        }),
        _getContractAdditionalInfo(contractId).catch((error) => {
          this.ShowToast('Alerta', `${error.message}.`, 'error');
          return { data: null };
        }),
      ]);
      this.setActiveViewDisplay(
        requestHistoryViewDisplay.MODIFY_APPROVAL_DATE,
        index,
        { submittedContractDate, additionalInformation }
      );
    },
    setActiveViewDisplay(viewDisplay, index, prop) {
      this.results = this.results.map((x, idx) => ({
        ...x,
        isOpen: idx === index,
        viewToDisplay: idx === index ? viewDisplay : contractViewDisplay.UNSET,
        ...(idx === index ? prop : {}),
      }));
    },
    onOpenCloseHandler(index, row) {
      if (row.isOpen) {
        this.setActiveViewDisplay(contractViewDisplay.UNSET, index, {});
        this.results = this.results.map((x) => ({
          ...x,
          isOpen: false,
        }));
      } else {
        this.results = this.results.map((x, rowIndex) => ({
          ...x,
          isOpen: rowIndex === index,
        }));
      }
    },
    async onOpenAdditionalInfoTabHandler(index, { contractId }) {
      if (contractId) {
        const { data } = await _getContractAdditionalInfo(contractId).catch(
          (error) => {
            this.ShowToast('Alerta', `${error.message}.`, 'error');
            return { data: null };
          }
        );
        this.setActiveViewDisplay(
          contractViewDisplay.ADDITIONAL_INFORMATION,
          index,
          { additionalInformation: data }
        );
      }
    },
    async onOpenStatusHistoryTabHandler(
      index,
      { contractId },
      pageIndex,
      pageSize
    ) {
      if (contractId) {
        const response = await this.getContractStatusHistory(
          contractId,
          pageIndex,
          pageSize
        );
        this.setActiveViewDisplay(
          contractViewDisplay.CONTRACT_STATUS_HISTORY,
          index,
          { contractStatusHistory: response }
        );
      }
    },
    async onOpenCostChangeHistoryTabHandler(
      index,
      { contractId },
      pageIndex,
      pageSize
    ) {
      if (contractId) {
        const response = await this.getContractCostChangeHistory(
          contractId,
          pageIndex,
          pageSize
        );
        this.setActiveViewDisplay(
          contractViewDisplay.CONTRACT_COST_CHANGE_HISTORY,
          index,
          { contractCostChangeHistory: response }
        );
      }
    },
    async onOpenHistoryTabHandler(
      index,
      { contractId },
      pageIndex,
      pageSize = DEFAULT_HISTORY_PAGE_SIZE
    ) {
      if (contractId) {
        const response = await this.getContractHistory(
          contractId,
          pageIndex,
          pageSize
        );
        this.setActiveViewDisplay(contractViewDisplay.CONTRACT_HISTORY, index, {
          contractHistory: {
            data: response.data,
            pagination: {
              totalItemCount: response.totalItemCount,
              pageCount: response.pageCount,
              currentPage: response.currentPage,
              count: response.count,
              pageSize,
            },
          },
        });
      }
    },
    async onOpenAeeTabHandler(index, { contractId }) {
      if (contractId) {
        const response = await this.getRequestExtendedAcademicYear(contractId);
        this.setActiveViewDisplay(contractViewDisplay.CONTRACT_AEE, index, {
          requestAee: response,
        });
      }
    },
    async onOpenCompensatoryRequestTabHandler(
      index,
      { contractId },
      pageIndex,
      pageSize = DEFAULT_HISTORY_PAGE_SIZE
    ) {
      if (contractId) {
        const excludedStatusIds = [
          compensatoryRequestStatus.SolicitudRechazada,
          compensatoryRequestStatus.SolicitudCompletada,
        ];
        const response = await this.searchByContractId(
          contractId,
          excludedStatusIds,
          pageIndex,
          pageSize
        );
        const results = response.data.map((x) => ({
          ...x,
          isOpen: false,
          documents: [],
          strategies: [],
          viewToDisplay: contractViewDisplay.ADDITIONAL_INFORMATION,
        }));

        this.setActiveViewDisplay(
          contractViewDisplay.COMPENSATORY_REQUEST,
          index,
          {
            compensatoryRequest: {
              data: results,
              isEnabled: this.aeeFeaturesEnabled,
              pagination: {
                totalItemCount: response.totalItemCount,
                pageCount: response.pageCount,
                currentPage: response.currentPage,
                count: response.count,
                pageSize,
              },
            },
          }
        );
      }
    },
    async onOpenVisitDetailTabHandler(index, row) {
      const { data } = await _getContractVisitInfo(row.contractId);
      this.setActiveViewDisplay(contractViewDisplay.VISIT_DETAILS, index, {
        ...data,
      });
    },
    async onStatusHistoryLoadMoreHandler(index, row) {
      const pageIndex = row.contractStatusHistory.currentPage;
      const pageSize =
        row.contractStatusHistory.count + DEFAULT_HISTORY_STATUS_PAGE_SIZE;

      this.onOpenStatusHistoryTabHandler(index, row, pageIndex, pageSize);
    },
    async onCostChangeHistoryLoadMoreHandler(index, row) {
      const pageIndex = row.contractCostChangeHistory.currentPage;
      const pageSize =
        row.contractCostChangeHistory.count + DEFAULT_HISTORY_STATUS_PAGE_SIZE;

      this.onOpenCostChangeHistoryTabHandler(index, row, pageIndex, pageSize);
    },
    async onOpenDocumentsTabHandler(index, row) {
      const { data } = await _getAdditionalsDocumentTypes();
      const { data: documents } = await _getAdditionalContractDocuments(
        row.contractId,
        row.academicYearId
      );
      this.setActiveViewDisplay(
        contractViewDisplay.CONTRACT_ADDITIONAL_DOCUMENTS,
        index,
        {
          documentsTypes: data,
          documents,
        }
      );
    },
    async onOpenDocumentHistoryTabHandler(index, row) {
      const { data } = await _getContractDocumentsHistory(
        row.contractId,
        row.academicYearId
      );
      this.setActiveViewDisplay(
        contractViewDisplay.CONTRACT_DOCUMENT_HISTORY,
        index,
        {
          contractDocumentHistory: data,
        }
      );
    },
    async onOpenCompensatoryTherapyBalanceHandler(index, row) {
      const { data } = await _getBalanceOfTherapyCompensatory(
        row.contractId,
        true
      );
      this.setActiveViewDisplay(
        contractViewDisplay.COMPENSATORY_THERAPY_BALANCE,
        index,
        {
          compensatoryTherapyBalance: data,
        }
      );
    },
    async onOpenAddDiagnosisHandler(index, { studentSieId, disciplineTypeId }) {
      this.setActiveViewDisplay(
        contractViewDisplay.CONTRACT_ADD_DIAGNOSIS,
        index,
        {
          diagnosis: { studentSieId, disciplineTypeId },
        }
      );
    },
    async onHistoryChangePageHandler(index, row, pageIndex) {
      this.onOpenHistoryTabHandler(index, row, pageIndex);
    },
    async onCompensatoryRequestChangePageHandler(index, row, pageIndex) {
      this.onOpenCompensatoryRequestTabHandler(index, row, pageIndex);
    },
    onRefreshAdditionalDocumentsHandler(index, row) {
      this.onOpenDocumentsTabHandler(index, row);
    },
    async onCompleteEvaluationHandler(index, { contractId }) {
      const {
        data: initEvaluationDocument,
      } = await _getContractInitEvaluationDocument(contractId).catch(
        (error) => {
          this.ShowToast('Alerta', `${error.message}.`, 'error');
          return { data: null };
        }
      );
      this.setActiveViewDisplay(
        contractViewDisplay.COMPLETE_EVALUATION,
        index,
        { initEvaluationDocument }
      );
    },
    async onViewEvaluationDocumentsHandler(index, refreshResult) {
      if (refreshResult) {
        await this.searchContracts(); //refresh result
      }

      this.$refs['contra-row-ref'][index].showContractHistoryTab = true;
      this.setActiveViewDisplay(
        contractViewDisplay.EVALUATION_DOCUMENTS,
        index,
        {}
      );
    },
    async onViewServiceCertification({
      contractId,
      studentSieId,
      specialistId,
      requestServiceTypeId,
      companyId,
      academicYearId,
      yearNumber,
      draftedDateMonthNumber,
      specialistOrAssistantList,
    }) {
      draftedDateMonthNumber = draftedDateMonthNumber
        ? draftedDateMonthNumber
        : 0;
      const payload = {
        contractId,
        studentSieId,
        specialistId: specialistId || specialistOrAssistantList[0]?.id,
        companyId,
        academicYearId,
        yearNumber,
        serviceTypeId: requestServiceTypeId,
        monthNumber: draftedDateMonthNumber,
      };

      await ReportServices.getVisitCertification([{ ...payload }])
        .then((res) => utils.convertToBlob(res.data))
        .catch((err) => {
          this.ShowToast('', err.response?.data?.message, 'error');
        });
    },
    async getAEESysParameter() {
      const { data } = await getByParameterName(
        'RPExtendedSchoolYearFeatureFlag'
      );
      if (data) {
        this.aeeFeaturesEnabled = data.value === 'true';
      }
    },
    async getAddDiagnosisSysParameter() {
      const { data } = await getSysparameterByNames({
        parameterNames: ['MedicaidPilotCompanyIds', 'CanAddDiagnosisRp'],
      });

      this.canAddDiagnosis =
        data
          ?.find((x) => x.name === 'CanAddDiagnosisRp')
          ?.staticValue?.toLowerCase() == 'true' ?? false;

      this.medicaidPilotCompanyIds =
        data
          ?.find((x) => x.name === 'MedicaidPilotCompanyIds')
          ?.staticValue?.split(',')
          .map((x) => parseInt(x, 10)) ?? [];
    },
    onAddInterventionPlanHandler(row) {
      const isSubmitable =
        this.isTherapyService(row) &&
        row.contractStatusId === rpContractStatus.Signed;
      this.$router.push({
        name: INTERVENTION_PLAN_MANAGEMENT_ROUTE_NAME,
        params: {
          contractId: row.planContractId,
          isSubmitable: isSubmitable,
          planId: row.planId,
        },
      });
    },
    onViewAchievementSummary(row) {
      const isSubmitable =
        this.isTherapyService(row) &&
        row.contractStatusId === rpContractStatus.Signed;
      this.$router.push({
        name: ACHIEVEMENT_SUMMARY_ROUTE_NAME,
        params: {
          contractId: row.planContractId,
          planId: row.planId,
          isAchievementSummary: true,
          isSubmitable: isSubmitable,
        },
      });
    },
    async getCanRegisterInterventionPlanParameter() {
      const { data } = await getByParameterName('RP_ShowInterventionPlan');
      if (data) {
        this.canRegisterInterventionPlan = data.value === 'true';
      }
    },
  },
};
</script>
